import { DotLegalDeleteDialog, DotLegalInfoDialog, DotLegalMultiSelect } from "@dotlegal/dotlegal-ui-components";
import React, { useState } from "react";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery.ts";
import { AccessControlEntity, useAccessResponsibilityHelper } from "./useAccessResponsibilityHelper.hooks.ts";
import { UserSelectableItem } from "../../user/User.types.ts";
import { Trans } from "react-i18next";

export interface ExcludedUsersFieldProps<T extends AccessControlEntity> {
    entity: T | undefined;
    isLoading: boolean;
    onChange: (changeFunc: (entity: T) => void) => Promise<void>;
    onConfirmNavigate: () => void;
    onRemoveFromExcluded: (userId: string) => Promise<void>;
    removeFromExcludedInProgress: boolean;
    readOnly?: boolean;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
    toolTipText?: string;
    warningText?: string;
    disableAccessWarning?: boolean;
}

function ExcludedUsersField<T extends AccessControlEntity>(props: ExcludedUsersFieldProps<T>) {
    const [removeUserFromExcludedListChanges, setRemoveUserFromExcludedListChanges] = useState<T | null>(null);
    const [userToRemoveFromExcludedList, setUserToRemoveFromExcludedList] = useState<UserSelectableItem | null>(null);

    const { translateString } = useTranslation();
    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useAccessResponsibilityHelper<T>(
        props.onChange,
        props.onConfirmNavigate,
        props.disableAccessWarning
    );

    const userQuery = useResponsibleQuery(!props.isPreview);

    async function onConfirmRemoveUserFromExcludedList() {
        await props.onRemoveFromExcluded(userToRemoveFromExcludedList!.id);

        await props.onChange((entity: T) => {
            Object.assign(entity, removeUserFromExcludedListChanges!);
        });

        setRemoveUserFromExcludedListChanges(null);
        setUserToRemoveFromExcludedList(null);
    }

    return (
        <>
            <DotLegalMultiSelect
                options={
                    props.entity === undefined
                        ? []
                        : (userQuery.dataByList(props.entity.excludedUsers ?? []) ?? [])
                              .filter((x) => x.id !== props.entity!.responsibleId && !props.entity?.additionalAccessUsers.includes(x.id))
                              .map((x) => ({ ...x, name: `${x.name}${x.active ? "" : ` (${translateString("deactivated")})`}` }))
                }
                isLoading={(userQuery.isLoading || props.isLoading) && !props.readOnly}
                onChange={() => {}}
                label={translateString("excludedUsers")}
                noOptionsLabel={translateString("noOptions")}
                warningText={props.isPreview ? undefined : props.warningText}
                toolTipText={props.toolTipText}
                chosenOptions={props.entity?.excludedUsers ?? []}
                disabled={props.disabled}
                errorText={props.errorText}
                onItemDeleted={
                    props.readOnly
                        ? () => {}
                        : (removedUser) => {
                              let users = props.entity!.excludedUsers.filter((excludedUser) => excludedUser !== removedUser);
                              let newEntity = { ...props.entity!, excludedUsers: users };

                              setRemoveUserFromExcludedListChanges(newEntity);
                              const userBeingRemoved = userQuery.userData?.find((x) => x.id === removedUser);
                              if (userBeingRemoved) {
                                  setUserToRemoveFromExcludedList(userBeingRemoved);
                              }
                          }
                }
                onItemAdded={
                    props.readOnly
                        ? () => {}
                        : (addedUser) => {
                              let newEntity = { ...props.entity!, excludedUsers: [...props.entity!.excludedUsers, addedUser] };
                              onFieldChange(newEntity, props.entity!);
                          }
                }
            />

            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey="confirmSystemAccessRemovalBody"
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}

            {removeUserFromExcludedListChanges && userToRemoveFromExcludedList && (
                <DotLegalInfoDialog
                    header={translateString("removeFromExcluded")}
                    btnText={translateString("removeFromExcluded")}
                    content={translateString("removeFromExcludedInformationText", { fullName: userToRemoveFromExcludedList.name })}
                    onCloseDialog={() => {
                        setRemoveUserFromExcludedListChanges(null);
                        setUserToRemoveFromExcludedList(null);
                    }}
                    onBtnClick={() => onConfirmRemoveUserFromExcludedList()}
                    inProgress={props.removeFromExcludedInProgress}
                />
            )}
        </>
    );
}

export default ExcludedUsersField;
